import gtag, { gtag4Event, GtagConfig } from '../gtag';
import { trackEventFacebook, trackPageViewFacebook } from './facebook';
import { TrackPurchaseEventParams } from './types';

/**
 * Wrapper function for RudderStack's `track` method
 * @param eventName event type
 * @param properties event properties (custom params)
 */
function rudderstackEvent(
  eventName: string,
  properties: Record<string, any> = {},
): void {
  const { rudderanalytics } = window;
  if (rudderanalytics && !Array.isArray(rudderanalytics) && typeof rudderanalytics.track === 'function') {
    rudderanalytics.track(eventName, properties);
  }
}

/**
 * Wrapper function for RudderStack's `page` method to track page views
 * @param name optional page name
 * @param properties optional page properties
 */
function rudderstackPage(
  name?: string,
  properties?: Record<string, any>,
): void {
  const { rudderanalytics } = window;
  if (rudderanalytics && !Array.isArray(rudderanalytics) && typeof rudderanalytics.page === 'function') {
    rudderanalytics.page(name, properties);
  }
}

/**
 * Wrapper function for RudderStack's `identify` method to associate the current user
 * @param userId unique user id (e.g. from auth)
 * @param traits optional user traits
 */
export function rudderstackIdentify(
  userId: string,
  traits?: Record<string, any>,
): void {
  const { rudderanalytics } = window;
  if (rudderanalytics && !Array.isArray(rudderanalytics) && typeof rudderanalytics.identify === 'function') {
    rudderanalytics.identify(userId, traits);
  }
}

export function trackCheckout(tripId: number, eventCategory: 'checkout' | 'instabook checkout'): void {
  gtag('event', 'Click to checkout', {
    event_category: eventCategory,
  });
  trackEventFacebook('InitiateCheckout', { content_ids: [tripId] });
}

let lastIdentifiedUserId: string | null = null;

/**
 * Tracks login and sets user_id for analytics (RudderStack identify, gtag).
 * Only sends when userId differs from the last identified user (deduplicated).
 */
export function trackLogin(userId: string): void {
  if (typeof window === 'undefined' || userId === null || userId === '') return;
  if (userId === lastIdentifiedUserId) return;
  lastIdentifiedUserId = userId;
  rudderstackIdentify(userId);
  gtag('set', { user_id: userId });
}

/** Clears the last identified user (call on logout). */
export function clearTrackLoginUser(): void {
  lastIdentifiedUserId = null;
}

export function trackRegistration() {
  gtag('event', 'New Registration', {
    event_category: 'Authentication',
  });
  trackEventFacebook('CompleteRegistration');
}

/**
 * Sends an event to multiple analytics platforms (GA4 and RudderStack)
 * @param eventName event type
 * @param config event parameters (custom params)
 */
export function trackEvent(
  eventName: string,
  config: GtagConfig = {},
): void {
  // Send to GA4
  gtag4Event(eventName, config);

  // Send to Rudderstack
  rudderstackEvent(eventName, config);
}

/**
 * Manually track a page view. Most often used as a result of a router change.
 * This tracks page views for Facebook Pixel and RudderStack.
 */
export function trackPageView(): void {
  // Track page view for Facebook Pixel
  trackPageViewFacebook();

  // Track page view for RudderStack
  // Get current page info for better tracking
  const pageName = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const pageProperties = typeof window !== 'undefined' ? {
    path: window.location.pathname,
    url: window.location.href,
    title: document.title,
    referrer: document.referrer || undefined,
  } : undefined;

  rudderstackPage(pageName, pageProperties);
}

/**
 * Send the purchase event via Gtag GTM and onwards to Google Analytics
 * @param params Booking transaction details and locale
 */
export function trackPurchaseEvent({
  transactionId,
  threadId,
  bookingTotal,
  currency,
  locale,
  boatId,
  boatHeadline,
  initialLandingPage,
  isInstabook,
  isDirectBooking,
  freeCancellation,
}: TrackPurchaseEventParams) {
  let purchaseType = isInstabook ? 'marketplace_instabook' : 'marketplace';
  if (isInstabook && isDirectBooking) { purchaseType = 'book-now-direct'; }

  trackEvent('purchase', {
    transaction_id: transactionId,
    affiliation: 'GMB Booking',
    event_category: 'checkout',
    value: bookingTotal,
    currency,
    tax: 0,
    tax_rate: 0,
    shipping: 0,
    purchase_type: purchaseType,
    locale,
    items: [{
      id: boatId,
      item_id: boatId,
      item_name: boatHeadline,
      quantity: 1,
      price: bookingTotal,
    }],
    p1: initialLandingPage?.url,
    p2: initialLandingPage?.referrer,
    p3: initialLandingPage?.gclid,
    p4: initialLandingPage?.utmValues?.utm_source,
    p5: initialLandingPage?.utmValues?.utm_medium,
    p6: initialLandingPage?.utmValues?.utm_campaign,
    p7: initialLandingPage?.utmValues?.utm_term,
    p8: initialLandingPage?.utmValues?.utm_content,
    p9: freeCancellation,
    thread_id: threadId.toString(),
  });
}
